import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { ArrowRight } from 'lucide-react';
import { CONTEXT_OPTIONS } from '../lib/pillars';

export const ContextSelect = ({ onSelect, onBack }) => {
  const [selected, setSelected] = useState(null);
  const [customLabel, setCustomLabel] = useState('');

  const canContinue = selected && (selected !== 'custom' || customLabel.trim().length > 0);

  const handleContinue = () => {
    const option = CONTEXT_OPTIONS.find(o => o.value === selected);
    const label = selected === 'custom' ? customLabel.trim() : option?.label;
    onSelect({ context: selected, label });
  };

  return (
    <section className="min-h-screen py-24 px-6" data-testid="context-section">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
            Step 1
          </span>
          <h2 className="text-2xl sm:text-3xl font-light text-foreground mt-2 mb-3">
            Where do you want to understand your experience?
          </h2>
          <p className="text-muted-foreground text-sm">
            Different situations can feel completely different. Choose the context you want to focus on.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-3"
        >
          {CONTEXT_OPTIONS.map((option) => (
            <Card
              key={option.value}
              onClick={() => setSelected(option.value)}
              className={`p-4 cursor-pointer transition-all border ${
                selected === option.value
                  ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                  : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
              }`}
              data-testid={`context-option-${option.value}`}
            >
              <span className="text-foreground font-light block">{option.label}</span>
              <span className="text-xs text-muted-foreground mt-0.5 block">{option.description}</span>
            </Card>
          ))}

          {selected === 'custom' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="pt-3"
            >
              <label className="text-xs font-medium text-muted-foreground block mb-2">
                Describe the situation
              </label>
              <Input
                placeholder='e.g. "with a specific person," "in a group setting," "long-distance," etc.'
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                className="bg-card"
                data-testid="custom-context-input"
                autoFocus
              />
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="flex justify-between mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Button
            onClick={onBack}
            variant="ghost"
            className="rounded-full px-6"
            data-testid="context-back-btn"
          >
            Back
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!canContinue}
            className="rounded-full px-8"
            data-testid="context-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
